import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { 
  FaArrowLeft, 
  FaSpinner,
  FaTrash,
  FaEdit,
  FaPlus,
  FaStar
} from 'react-icons/fa';
import QuestionModal from './QuestionModal';
import ImportQuestionModal from './ImportQuestions'; // Import the modal component
import ExcelQuestionImportModal from './ExcelQuestionImportModal';
import MathJaxRenderer from './Mathjaxrenderer';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const TestQuestionsView = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  // State
  const [testName, setTestName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [totalMarks, setTotalMarks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false); // New state for import modal
  const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  
  // Fetch data on component mount
  useEffect(() => {
    if (!testId) {
      setError('Invalid test ID');
      setLoading(false);
      return;
    }
    
    fetchData();
  }, [testId]);
  
  // Calculate total marks
  useEffect(() => {
    if (questions.length > 0) {
      const total = questions.reduce((sum, question) => 
        sum + (question.marks || 1), 0);
      setTotalMarks(total);
    } else {
      setTotalMarks(0);
    }
  }, [questions]);
  
  // Fetch test data and questions
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch test details to get the name
      const testResponse = await axios.get(`${API_BASE_URL}/Tests/${testId}`);
      setTestName(testResponse.data.name || `Test ${testId}`);
      
      // Fetch question IDs for this test
      const questionsResponse = await axios.get(`${API_BASE_URL}/QuestionTestAssociations/test/${testId}/questions`);
      const questionIds = questionsResponse.data.question_ids;
      
      if (questionIds && questionIds.length > 0) {
        // Fetch questions in batches to avoid too many simultaneous requests
        const fetchedQuestions = [];
        
        // Process in batches of 10
        for (let i = 0; i < questionIds.length; i += 10) {
          const batch = questionIds.slice(i, i + 10);
          const batchPromises = batch.map(id => 
            axios.get(`${API_BASE_URL}/Questions/${id}`)
              .then(response => response.data)
              .catch(error => {
                console.error(`Error fetching question ${id}:`, error);
                return { 
                  question_id: id, 
                  question_text: 'Failed to load question', 
                  marks: 1,
                  error: true 
                };
              })
          );
          
          const batchResults = await Promise.all(batchPromises);
          fetchedQuestions.push(...batchResults);
        }
        
        setQuestions(fetchedQuestions);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      setError('Failed to load questions. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Navigate back to test view
  const goBack = () => {
    navigate(`/tests/${testId}`);
  };
  
  // Remove a question from the test
  const removeQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to remove this question from the test?')) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/QuestionTestAssociations/test/${testId}/remove-questions`, {
        data: { question_ids: [questionId] }
      });
      
      // Update the questions list after deletion
      setQuestions(questions.filter(q => q.question_id !== questionId));
      setLoading(false);
    } catch (err) {
      setError('Failed to remove question. Please try again.');
      console.error('Error removing question:', err);
      setLoading(false);
    }
  };
  
  // Open modal to add a new question
  const openAddQuestionModal = () => {
    setEditingQuestionId(null);
    setIsModalOpen(true);
  };
  
  // Open modal to import questions
  const openImportquestionModal = () => {
    setIsImportModalOpen(true);
  };
  
  // Open modal to edit an existing question
  const openEditQuestionModal = (questionId) => {
    setEditingQuestionId(questionId);
    setIsModalOpen(true);
  };
  
  // Handle successful question save
  const handleQuestionSaved = (savedQuestion) => {
    if (editingQuestionId) {
      // Update the question in the list
      setQuestions(questions.map(q => 
        q.question_id === savedQuestion.question_id ? savedQuestion : q
      ));
    } else {
      // Add the new question to the list
      setQuestions([...questions, savedQuestion]);
    }
  };
  
  // Handle successful question import
  const handleQuestionsImported = (importedQuestionIds) => {
    // Refresh the questions after import
    fetchData();
  };
  
  // Get option letter (A, B, C, D) based on option number
  const getOptionLetter = (optionNumber) => {
    return String.fromCharCode(65 + parseInt(optionNumber.split('_')[1]) - 1);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Back Navigation */}
      <div className="mb-6">
        <button 
          onClick={goBack}
          className="flex items-center text-gray-600 hover:text-gray-900 font-medium"
        >
          <FaArrowLeft className="mr-2" /> Back to Test
        </button>
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {testName} - Questions
          </h1>
          {totalMarks > 0 && (
            <div className="text-md text-gray-600 mt-1">
              Total Marks: {totalMarks}
            </div>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={openImportquestionModal}
            className="inline-flex items-center px-4 py-2 rounded-md text-white hover:opacity-90"
            style={{ backgroundColor: currentColor }}
          >
            <FaPlus className="mr-2" /> Import Question
          </button>
          <button
            onClick={() => setIsExcelImportOpen(true)}
            className="inline-flex items-center px-4 py-2 rounded-md text-white hover:opacity-90"
            style={{ backgroundColor: currentColor }}
            title="Import questions from an Excel (.xlsx) file"
          >
            <FaPlus className="mr-2" /> Import from Excel
          </button>
          <button
            onClick={openAddQuestionModal}
            className="inline-flex items-center px-4 py-2 rounded-md text-white hover:opacity-90"
            style={{ backgroundColor: currentColor }}
          >
            <FaPlus className="mr-2" /> Add Question
          </button>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && !questions.length && (
        <div className="flex justify-center items-center p-12">
          <FaSpinner className="animate-spin text-4xl text-gray-500" />
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 mb-6">
          <p>{error}</p>
        </div>
      )}
      
      {/* No Questions State */}
      {!loading && questions.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg shadow-md">
          <p className="text-gray-500 mb-4">No questions found for this test.</p>
          <button
            onClick={openAddQuestionModal}
            className="inline-flex items-center px-4 py-2 rounded-md text-white hover:opacity-90"
            style={{ backgroundColor: currentColor }}
          >
            <FaPlus className="mr-2" /> Add Your First Question
          </button>
        </div>
      )}
      
      {/* Questions List */}
      {questions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="p-4 bg-gray-50 border-b font-medium flex justify-between">
            <div>Questions ({questions.length})</div>
            <div>Total Marks: {totalMarks}</div>
          </div>
          
          <div className="divide-y">
            {questions.map((question, index) => (
              <div key={question.question_id} className="p-5 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className="font-medium text-gray-900 mr-2">
                      Question {index + 1}
                    </div>
                    <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-sm">
                      <FaStar className="mr-1 text-xs" /> {question.marks || 1} {question.marks === 1 ? 'mark' : 'marks'}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditQuestionModal(question.question_id)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Edit question"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => removeQuestion(question.question_id)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove question"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                
                {/* Question Text */}
                <div className="mb-4">
                  <MathJaxRenderer content={question.question_text} />
                </div>
                
                {/* Question Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {['option_1', 'option_2', 'option_3', 'option_4'].map((option) => (
                    question[option] && (
                      <div 
                        key={option}
                        className={`p-3 rounded-md border ${
                          question.correct_option === option 
                            ? 'bg-green-50 border-green-200 text-green-800' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <span className="font-medium mr-2">{getOptionLetter(option)}.</span>
                        <MathJaxRenderer content={question[option]} />
                      </div>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Question Modal */}
      <QuestionModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        testId={testId}
        questionId={editingQuestionId}
        onSuccess={handleQuestionSaved}
      />
      
      {/* Import Question Modal */}
      <ImportQuestionModal 
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        testId={testId}
        onSuccess={handleQuestionsImported}
        currentColor={currentColor}
      />

      {/* Excel (.xlsx) Import Modal */}
      <ExcelQuestionImportModal
        isOpen={isExcelImportOpen}
        onClose={() => setIsExcelImportOpen(false)}
        testId={testId}
        onImportSuccess={fetchData}
      />
    </div>
  );
};

export default TestQuestionsView;